import Link from "next/link";
import {
  HiOutlineCalculator,
  HiOutlineChevronUp,
  HiOutlineCog,
  HiOutlineDatabase,
  HiOutlineHome,
  HiOutlineLogout,
  HiOutlinePencil,
  HiOutlinePlusCircle,
  HiOutlineUserAdd,
  HiOutlineUserCircle,
} from "react-icons/hi";
import {
  AiOutlineFileSearch,
  AiOutlineBug,
  AiOutlineBank,
} from "react-icons/ai";
import { BiArrowToLeft, BiArrowToRight } from "react-icons/bi";

import useEnv from "../src/useEnv";
import useUser from "../src/useUser";
import useTenant, { longTenantDisplayName } from "../src/useTenant";
import { useTenantAction } from "../src/useAction";

import WithIcon from "./WithIcon";
import { useRouter } from "next/router";
import { useSegmentIdentify } from "../src/segment";
import Hyperlink from "./Hyperlink";
import { IconType } from "react-icons";
import { useEffect, useState } from "react";
import { useTransition } from "transition-hook";
import useOutsideClick from "../src/useOutsideClick";
import { BsArrowLeftRight } from "react-icons/bs";
import TenantKindBadge from "./TenantKindBadge";
import { Portal } from "@headlessui/react";
import createPersistedState from "@/src/createPersistedState";

const NotificationBadge: React.FunctionComponent<{ number: number }> = ({
  number,
}) => {
  return (
    <div
      className={`rounded-full text-white font-bold w-4 h-4 flex justify-center items-center text-xs ${
        number > 0 ? "bg-oso-purple" : "bg-gray-500"
      }`}
    >
      {number}
    </div>
  );
};

const ExpandedUserPanelLink: React.FunctionComponent<
  {
    name: string;
    icon: IconType;
    badge?: number;
  } & ({ href: string } | { onClick: () => void })
> = (props) => {
  const router = useRouter();
  let content = (
    <WithIcon icon={<props.icon />} iconClassName="text-lg">
      {props.name}
    </WithIcon>
  );
  let className =
    "transition-colors text-gray-700 hover:text-gray-900 hover:bg-gray-300 px-4 py-1";
  if (props.badge !== undefined) {
    className += " flex justify-between items-center";
    content = (
      <>
        {content}
        <NotificationBadge number={props.badge} />
      </>
    );
  }
  if ("href" in props) {
    if (router.pathname === props.href) {
      className += " border-r-2 border-gray-600 font-semibold";
    }
    return (
      <Hyperlink className={className} href={props.href}>
        {content}
      </Hyperlink>
    );
  } else {
    return (
      <button
        className={className}
        onClick={(e) => {
          e.preventDefault();
          props.onClick();
        }}
      >
        {content}
      </button>
    );
  }
};

const ExpandedUserPanel: React.FunctionComponent<{
  isOpen: boolean;
}> = ({ isOpen }) => {
  const { shouldMount, stage } = useTransition(isOpen, 150);
  const { switchTenant } = useTenant();
  const { user, logout } = useUser();

  const stageIsEnter = stage === "enter";
  const numberOfInvitations = user?.pending_invitations.length ?? 0;
  if (!shouldMount) return null;
  return (
    <Portal>
      <div
        className={`fixed left-2 bottom-12 py-2 rounded-lg shadow-lg z-10 w-[250px] transition-all flex flex-col bg-gray-200 ${
          stageIsEnter
            ? "opacity-100 scale-100 translate-x-0 translate-y-0"
            : "opacity-0 scale-95 translate-y-2"
        }`}
      >
        <ExpandedUserPanelLink
          href="/settings"
          icon={HiOutlineCog}
          name="Organization Settings"
        />
        <ExpandedUserPanelLink
          onClick={switchTenant}
          icon={BsArrowLeftRight}
          name="Switch Environment"
        />
        <ExpandedUserPanelLink
          href="/user-settings"
          icon={HiOutlineUserAdd}
          name="Invitations"
          badge={numberOfInvitations}
        />
        <ExpandedUserPanelLink
          onClick={logout}
          icon={HiOutlineLogout}
          name="Log Out"
        />
      </div>
    </Portal>
  );
};

const UserPanel: React.FunctionComponent<{ collapsed: boolean }> = ({
  collapsed,
}) => {
  const { user } = useUser();
  const [isExpanded, setIsExpanded] = useState(false);
  const outsideClickRef = useOutsideClick<HTMLDivElement>(() =>
    setIsExpanded(false)
  );

  if (!user) return null;
  const thereArePendingInvitations = user.pending_invitations.length > 0;
  return (
    <div className="px-4 py-2 bg-gray-200 text-gray-900 text-sm h-10 relative">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }}
        ref={outsideClickRef}
      >
        {collapsed ? (
          <HiOutlineUserCircle className="text-2xl" />
        ) : (
          <WithIcon icon={<HiOutlineUserCircle />} iconClassName="text-2xl">
            {user.login_identifier}
          </WithIcon>
        )}
        <div
          className={`hover:bg-gray-300 p-2 -m-2 rounded-full transition-all ${
            isExpanded ? "rotate-180 bg-gray-300" : "rotate-0"
          } ${thereArePendingInvitations ? "border border-oso-purple" : ""}`}
        >
          <HiOutlineChevronUp />
        </div>
        {thereArePendingInvitations && (
          <div className="absolute top-0 right-0">
            <NotificationBadge number={user.pending_invitations.length} />
          </div>
        )}
      </div>
      <ExpandedUserPanel isOpen={isExpanded} />
    </div>
  );
};

const LeastPrivilegeCallout: React.FunctionComponent = () => {
  return (
    <div className="self-center">
      <a
        href="https://www.osohq.com/meet-the-oso-founder-ceo?utm_source=ui&utm_medium=ui&utm_campaign=14/11"
        title="Click to schedule a conversation"
        className=""
        target="_blank"
        rel="noopener noreferrer"
      >
        <div
          className="bg-get-started-actor hover:scale-105 transition-all rounded text-sm w-48 py-2 px-4"
          style={{
            background:
              "radial-gradient(circle,rgba(230, 215, 242, 1) 0%, rgba(221, 211, 237, 1) 100%)",
          }}
        >
          <div className="mb-1">
            <div className="font-semibold mb-1">
              Working on AI agents in production?
            </div>
            Schedule a meeting to see what we&apos;re building.
          </div>
        </div>
      </a>
    </div>
  );
};

const EnvironmentInfo: React.FunctionComponent = () => {
  const { selectedEnv } = useEnv();
  const { tenant, tenants } = useTenant();
  if (!selectedEnv || !tenant) return null;

  // if the user only has one environment, there's no need to show environment info
  const numberOfAvailableEnvironments = tenants.flatMap(
    ({ environments }) => environments
  ).length;
  if (numberOfAvailableEnvironments < 2) return null;

  // if the user only has one organization, then we don't need to show the organization name
  const numberOfAvailableTenants = tenants.length;

  return (
    <div className="bg-gray-200 self-center rounded py-2 px-4 m-2 text-sm">
      {numberOfAvailableTenants > 1 && (
        <div className="text-center mb-1 text-balance w-48">
          {longTenantDisplayName(tenant)}
        </div>
      )}
      <div className="flex items-center justify-around space-x-2">
        <TenantKindBadge kind={tenant.kind} />
        <div className="font-semibold">{selectedEnv.name}</div>
      </div>
    </div>
  );
};

function classnames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const Sidebar: React.FunctionComponent<{
  collapsed: boolean;
  setCollapsed: any;
}> = ({ collapsed, setCollapsed }) => {
  useSegmentIdentify();
  const { tenant } = useTenant();
  const viewBillingAction = useTenantAction("view_billing");
  const showBilling = tenant?.kind === "production" && viewBillingAction;

  const links = [
    { href: "/dashboard", name: "Dashboard", icon: HiOutlineHome },
    { href: "/rules-editor", name: "Rules", icon: HiOutlinePencil },
    { href: "/facts", name: "Data", icon: HiOutlineDatabase },
    { href: "/explain", name: "Explain", icon: AiOutlineBug },
    { href: "/logs", name: "Logs", icon: AiOutlineFileSearch },
    { href: "/install", name: "Install", icon: HiOutlinePlusCircle },
    { href: "/usage", name: "Usage", icon: HiOutlineCalculator },

    ...(showBilling
      ? [{ href: "/billing", name: "Billing", icon: AiOutlineBank }]
      : []),
  ];

  return (
    <div
      className={classnames(
        `fixed z-10 top-0 bottom-0 left-0 ${
          collapsed ? "" : "w-[150]px"
        } bg-gray-100 hidden md:block`
      )}
    >
      <div className="flex flex-col h-full">
        <div className="px-6 py-8">
          <Link href="/dashboard">
            <a className="flex space-x-2 items-center">
              <img
                alt="Oso Logo"
                src="/logo-bear-black.png"
                className={collapsed ? "h-4 mt-2 mb-1" : "h-7"}
              />
              <div
                className={`font-space text-xl ${collapsed ? "hidden" : ""}`}
              >
                <span>Oso</span> <span className="font-light">Cloud</span>
              </div>
            </a>
          </Link>
        </div>

        <div className="flex flex-col h-full">
          {links.map((link) => (
            <NavigationLink key={link.href} {...link} collapsed={collapsed} />
          ))}
          {!collapsed && (
            <>
              <div className="grow" />
              <LeastPrivilegeCallout />
              <EnvironmentInfo />
            </>
          )}
        </div>
        <UserPanel collapsed={collapsed} />

        <hr className="border-gray-300 my-2" />

        <button
          className={classnames("transition-all px-2 text-gray-500")}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <BiArrowToRight className="text-2xl" />
          ) : (
            <BiArrowToLeft className="text-2xl float-right" />
          )}
        </button>
      </div>
    </div>
  );
};

const NavigationLink: React.FC<{
  href: string;
  icon: IconType;
  name: string;
  collapsed?: boolean;
}> = ({ href, name, icon: Icon, collapsed }) => {
  const router = useRouter();
  const isActive = router.pathname === href;
  const className =
    isActive && !collapsed ? "border-r-2 border-gray-600 font-semibold" : "";
  return (
    <Hyperlink
      className="relative transition-colors text-gray-700 hover:text-gray-900 text-lg"
      href={href}
    >
      <div
        className={classnames(
          "group transition-colors hover:bg-gray-200 px-6 py-1",
          className,
          collapsed ? "py-2" : ""
        )}
      >
        {collapsed ? (
          <>
            <span className="flex items-center justify-center">
              <span className="text-xl">{<Icon />}</span>
            </span>
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 pointer-events-none">
              <div className="relative px-2 py-2 bg-gray-700 text-white text-lg rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                {name}
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -ml-2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-gray-700"></div>
              </div>
            </div>
          </>
        ) : (
          <WithIcon icon={<Icon />} iconClassName="text-xl">
            {!collapsed && name} {/* Show name only when not collapsed */}
          </WithIcon>
        )}
      </div>
    </Hyperlink>
  );
};

const useCollapsed = createPersistedState<boolean | null>("sidebar_collapsed");

export const WithSidebar: React.FunctionComponent = ({ children }) => {
  const [collapsed, setCollapsed] = useCollapsed(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  useEffect(() => {
    if (collapsed !== null) {
      setSidebarCollapsed(collapsed);
    }
  }, [collapsed, setCollapsed, setSidebarCollapsed]);
  return (
    <div className={sidebarCollapsed ? "md:pl-[75px]" : "md:pl-[250px]"}>
      <Sidebar collapsed={sidebarCollapsed} setCollapsed={setCollapsed} />
      {children}
    </div>
  );
};
